import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { hrms, getFileUrl } from '../../services/api';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { SearchableSelect } from '../../components/common/SearchableSelect';
import { theme } from '../../theme';

function EmployeeList() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlFilter = new URLSearchParams(location.search).get('filter'); // 'new_joiners' | 'probation_due'

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterProfileStatus, setFilterProfileStatus] = useState('');
  const [stats, setStats] = useState({ not_submitted: 0, pending_review: 0 });
  const pageSize = 20;

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (search) params.search = search;
      if (filterDept) params.department_id = filterDept;
      if (filterTeam) params.team_id = filterTeam;
      if (filterStatus) params.status = filterStatus;
      if (filterProfileStatus) params.profile_status = filterProfileStatus;
      const res = await hrms.getEmployees(params);
      const data = res.data;
      const empList = Array.isArray(data) ? data : (data.items || data.results || []);
      if (Array.isArray(data)) {
        setEmployees(data);
        setTotalCount(data.length);
      } else {
        setEmployees(empList);
        setTotalCount(data.total || data.count || 0);
      }
      setStats({
        not_submitted: empList.filter(e => !e.profile_status || e.profile_status === 'not_submitted').length,
        pending_review: empList.filter(e => e.profile_status === 'pending_review').length,
      });
    } catch (err) {
      console.warn('[EmployeeList] Failed to load employees:', err?.message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterDept, filterTeam, filterStatus, filterProfileStatus]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    hrms.getDepartments().then((r) => setDepartments(r.data || [])).catch(err => console.warn('[HRMS]', err?.message));
    hrms.getTeams().then((r) => setTeams(r.data || [])).catch(() => {});
  }, []);

  const handleDeactivate = async (emp) => {
    if (!window.confirm(`Deactivate ${emp.full_name}?`)) return;
    try {
      await hrms.updateEmployee(emp.id, { employment_status: 'inactive' });
      fetchEmployees();
    } catch (err) {
      alert('Failed to deactivate employee.');
    }
  };

  const columns = [
    { header: 'Emp Code', key: 'emp_code', width: '110px' },
    {
      header: 'Name',
      key: 'full_name',
      render: (val, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <div
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              background: theme.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: theme.primaryDark,
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {row.profile_photo ? (
              <img src={getFileUrl(row.profile_photo)} alt={val} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              val?.charAt(0)?.toUpperCase() || '?'
            )}
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>{val}</p>
            <p style={{ margin: 0, fontSize: '0.75rem', color: theme.textMuted }}>{row.email}</p>
          </div>
        </div>
      ),
    },
    { header: 'Department', key: 'department_name', render: (val) => val || '—' },
    { header: 'Position',   key: 'position_name',   render: (val) => val || '—' },
    { header: 'Team',       key: 'team_name',        render: (val) => val
        ? <span style={{ fontSize: '0.75rem', fontWeight: 600, background: '#EDE9FE', color: '#6D28D9', padding: '0.15rem 0.5rem', borderRadius: '0.25rem' }}>{val}</span>
        : <span style={{ color: theme.textMuted }}>—</span>
    },
    { header: 'Phone', key: 'phone', render: (val) => val || '—' },
    {
      header: 'Status',
      key: 'employment_status',
      render: (val) => <Badge status={val || 'active'} />,
    },
    {
      header: 'Profile Status',
      key: 'profile_status',
      render: (val) => {
        const map = {
          not_submitted: { label: 'Not Submitted', color: '#92400E', bg: '#FEF3C7' },
          pending_review: { label: 'Pending Review', color: '#1D4ED8', bg: '#DBEAFE' },
          approved:       { label: 'Approved',       color: '#065F46', bg: '#D1FAE5' },
          rejected:       { label: 'Rejected',       color: '#991B1B', bg: '#FEE2E2' },
        };
        const s = map[val || 'not_submitted'] || map.not_submitted;
        return <span style={{ fontSize: '0.72rem', fontWeight: 700, background: s.bg, color: s.color, padding: '0.2rem 0.5rem', borderRadius: '0.25rem' }}>{s.label}</span>;
      },
    },
    {
      header: 'Actions',
      key: 'id',
      align: 'right',
      render: (val, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => navigate(`/hrms/employees/${val}`)}>
            View
          </Button>
          <Button size="sm" variant="info" onClick={() => navigate(`/hrms/employees/${val}/edit`)}>
            Edit
          </Button>
          {row.employment_status !== 'inactive' && (
            <Button size="sm" variant="danger" onClick={() => handleDeactivate(row)}>
              Deactivate
            </Button>
          )}
        </div>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  // Apply client-side filter for URL-driven views
  const today = new Date();
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const probationCutoff = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const probationStart  = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()); // ~1yr ago

  const displayEmployees = !urlFilter ? employees : employees.filter(e => {
    if (urlFilter === 'new_joiners') {
      const hd = e.hire_date ? new Date(e.hire_date) : null;
      return hd && hd >= thisMonthStart;
    }
    if (urlFilter === 'probation_due') {
      const hd = e.hire_date ? new Date(e.hire_date) : null;
      if (!hd) return false;
      const daysSince = (today - hd) / (1000 * 60 * 60 * 24);
      return daysSince >= 60 && daysSince <= 90; // probation ending within 30 days of 90-day mark
    }
    return true;
  });

  const FILTER_BANNERS = {
    new_joiners:   { label: '🎉 Showing: New Joiners This Month', color: '#065F46', bg: '#ECFDF5', border: '#6EE7B7' },
    probation_due: { label: '⏰ Showing: Employees with Probation Due in 30 Days', color: '#92400E', bg: '#FFF7ED', border: '#FDE68A' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Employees</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {totalCount} total employees
          </p>
        </div>
        <Button onClick={() => navigate('/hrms/employees/new')} variant="primary">
          + Add Employee
        </Button>
      </div>

      {/* URL-driven filter banner */}
      {urlFilter && FILTER_BANNERS[urlFilter] && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: FILTER_BANNERS[urlFilter].bg, border: `1px solid ${FILTER_BANNERS[urlFilter].border}`, borderRadius: '0.5rem', padding: '0.625rem 1rem' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: FILTER_BANNERS[urlFilter].color }}>{FILTER_BANNERS[urlFilter].label} — {displayEmployees.length} found</span>
          <button onClick={() => navigate('/hrms/employees')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: FILTER_BANNERS[urlFilter].color, fontWeight: 600, textDecoration: 'underline' }}>Clear filter ×</button>
        </div>
      )}

      {/* Profile Status Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        {[
          { label: 'Profile Not Submitted', count: stats.not_submitted, color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', filter: 'not_submitted' },
          { label: 'Waiting for Verification', count: stats.pending_review, color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', filter: 'pending_review' },
        ].map(card => (
          <div key={card.label}
            onClick={() => { setFilterProfileStatus(prev => prev === card.filter ? '' : card.filter); setPage(1); }}
            style={{ background: filterProfileStatus === card.filter ? card.bg : theme.white, border: `1.5px solid ${filterProfileStatus === card.filter ? card.color : theme.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', cursor: 'pointer', transition: 'all 0.15s' }}>
            <p style={{ margin: '0 0 0.4rem', fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{card.label}</p>
            <p style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: card.color }}>{card.count}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <Card padding="1rem">
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by name, code, email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={inputStyle}
          />
          <select
            value={filterDept}
            onChange={(e) => { setFilterDept(e.target.value); setFilterTeam(''); setPage(1); }}
            style={{ ...inputStyle, maxWidth: '200px' }}
          >
            <option value="">All Departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <div style={{ minWidth: '180px' }}>
            <SearchableSelect
              options={[
                ...teams
                  .filter(t => !filterDept || String(t.department_id) === String(filterDept))
                  .map(t => ({ value: String(t.id), label: t.name }))
              ]}
              value={filterTeam}
              onChange={(val) => { setFilterTeam(val || ''); setPage(1); }}
              placeholder="All Teams"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            style={{ ...inputStyle, maxWidth: '160px' }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="on_leave">On Leave</option>
          </select>
          <select
            value={filterProfileStatus}
            onChange={(e) => { setFilterProfileStatus(e.target.value); setPage(1); }}
            style={{ ...inputStyle, maxWidth: '190px' }}
          >
            <option value="">All Profile Status</option>
            <option value="not_submitted">Not Submitted</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button variant="outlineGray" size="sm" onClick={() => { setSearch(''); setFilterDept(''); setFilterTeam(''); setFilterStatus(''); setFilterProfileStatus(''); setPage(1); }}>
            Clear
          </Button>
        </div>
      </Card>

      <Card padding="0">
        <Table columns={columns} data={displayEmployees} loading={loading} emptyMessage="No employees found" />
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem', borderTop: `1px solid ${theme.border}` }}>
            <Button variant="outlineGray" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
              ← Prev
            </Button>
            <span style={{ fontSize: '0.875rem', color: theme.textMuted }}>
              Page {page} of {totalPages}
            </span>
            <Button variant="outlineGray" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
              Next →
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

const inputStyle = {
  padding: '0.5rem 0.875rem',
  border: `1.5px solid ${theme.border}`,
  borderRadius: '0.5rem',
  fontSize: '0.875rem',
  color: theme.text,
  background: theme.white,
  outline: 'none',
  flex: 1,
  minWidth: '200px',
};

export default EmployeeList;
